import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { Blocker, BlockerFunction, useBlocker, useLocation, useNavigate } from 'react-router-dom';

type BlockerEntry = {
  shouldBlockFn: BlockerFunction;
  handler: (blocker: Blocker) => void;
  managed: boolean;
};

// Passed to a managed handler when we close it in response to a real POP (sentinel mode), where
// there is no react-router blocker to resolve — the handler only needs to run its onClose.
const NOOP_BLOCKER = {
  state: 'unblocked',
  proceed: undefined,
  reset: undefined,
  location: undefined
} as unknown as Blocker;

type NavigationBlockContextValue = {
  addBlocker: (
    key: string,
    shouldBlockFn: BlockerFunction,
    handler: (blocker: Blocker) => void,
    options?: { managed?: boolean }
  ) => void;
  removeBlocker: (key: string) => void;
  acquireBackGuard: () => void;
  releaseBackGuard: () => void;
};

const NavigationBlockContext = createContext<NavigationBlockContextValue>({
  addBlocker: () => {},
  removeBlocker: () => {},
  acquireBackGuard: () => {},
  releaseBackGuard: () => {}
});

const NavigationBlockContextProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const blockersRef = useRef<Map<string, BlockerEntry>>(new Map());
  const capturedKeysRef = useRef<string[]>([]);
  const [blockerCount, setBlockerCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  // First-page back-guard: on the very first SPA entry (history.state.idx === 0) a plain Back
  // unloads the document before react-router can intercept the POP, so the modal never closes.
  // We push one sentinel entry (ref-counted, shared by all managed modals) so Back becomes a
  // same-document POP. In that mode we deliberately DON'T block the POP — blocking would make
  // react-router fire a synchronous restore go(1) that races our proceed()/go(-1) and strands the
  // user on the sentinel. Instead we let Back pop the sentinel naturally and close the modal from
  // the popstate listener below. The blocker path is only for the idx>0 case (block + reset, stay).
  const firstPageGuardCountRef = useRef(0);
  const sentinelActiveRef = useRef(false);

  const blocker = useBlocker(
    useCallback(
      args => {
        const matched: string[] = [];
        blockersRef.current.forEach((entry, key) => {
          if (!entry.shouldBlockFn(args)) return;
          // Sentinel mode: let Back pop the sentinel; the popstate listener closes the modal.
          if (entry.managed && sentinelActiveRef.current) return;
          matched.push(key);
        });
        capturedKeysRef.current = matched;
        return matched.length > 0;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [blockerCount]
    )
  );

  useEffect(() => {
    const onPop = () => {
      if (!sentinelActiveRef.current) return;
      // Back popped the sentinel — we stay in the app on the real entry; close the managed modal(s).
      sentinelActiveRef.current = false;
      blockersRef.current.forEach(entry => {
        if (entry.managed) entry.handler(NOOP_BLOCKER);
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const keys = capturedKeysRef.current;
    capturedKeysRef.current = [];
    let hasManaged = false;
    let hasUnmanaged = false;
    keys.forEach(key => {
      const entry = blockersRef.current.get(key);
      if (!entry) return;
      entry.handler(blocker);
      if (entry.managed) hasManaged = true;
      else hasUnmanaged = true;
    });

    // Reaching here means a non-first-page modal (no sentinel): close it and stay put. Sentinel-mode
    // managed blockers never block (handled by the popstate listener above). If an unmanaged blocker
    // (e.g. verifx) also matched, it owns resolution — skip so we don't double-resolve the POP.
    if (hasManaged && !hasUnmanaged) {
      blocker.reset?.();
    }
  }, [blocker]);

  const addBlocker = useCallback(
    (
      key: string,
      shouldBlockFn: BlockerFunction,
      handler: (blocker: Blocker) => void,
      options?: { managed?: boolean }
    ) => {
      const isNewKey = !blockersRef.current.has(key);
      blockersRef.current.set(key, { shouldBlockFn, handler, managed: options?.managed ?? false });
      if (isNewKey) {
        setBlockerCount(c => c + 1);
      }
    },
    []
  );

  const removeBlocker = useCallback((key: string) => {
    if (!blockersRef.current.has(key)) return;
    blockersRef.current.delete(key);
    setBlockerCount(c => c - 1);
  }, []);

  const acquireBackGuard = useCallback(() => {
    firstPageGuardCountRef.current += 1;
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (firstPageGuardCountRef.current === 1 && !sentinelActiveRef.current && idx === 0) {
      sentinelActiveRef.current = true;
      const loc = locationRef.current;
      navigate(`${loc.pathname}${loc.search}${loc.hash}`, {
        state: { ...(loc.state as object), __navGuard: true }
      });
    }
  }, [navigate]);

  const releaseBackGuard = useCallback(() => {
    firstPageGuardCountRef.current = Math.max(0, firstPageGuardCountRef.current - 1);
    if (firstPageGuardCountRef.current !== 0) return;

    // Consume the leftover sentinel only when a manual close left us sitting on it. Read the LIVE
    // history entry (react-router nests user state under history.state.usr) rather than useLocation's
    // state, which updates a render behind and can be stale on a fast open→close. If Back already
    // consumed it (proceed) or the app navigated on while the modal was open, the marker is gone and
    // navigate(-1) would pop a real entry — so we just clear the flag instead.
    const onSentinel = (window.history.state as { usr?: { __navGuard?: boolean } } | null)?.usr
      ?.__navGuard;
    // Clear the flag first so the navigate(-1) popstate below doesn't re-trigger the close listener.
    const shouldConsume = sentinelActiveRef.current && onSentinel === true;
    sentinelActiveRef.current = false;
    if (shouldConsume) {
      navigate(-1);
    }
  }, [navigate]);

  return (
    <NavigationBlockContext.Provider
      value={{ addBlocker, removeBlocker, acquireBackGuard, releaseBackGuard }}
    >
      {children}
    </NavigationBlockContext.Provider>
  );
};

const useNavigationBlockContext = () => useContext(NavigationBlockContext);

export { NavigationBlockContextProvider, useNavigationBlockContext };
export type { NavigationBlockContextValue };
